import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  liveCounters,
  liveHighlights,
  landingCTAs,
  activityFeed,
  landingVivaSummary,
  getTopRadiosForLanding,
  type TrendPeriod,
  type LandingRankingEntry,
} from '@/mocks/rfwLandingViva';
import { getActiveCompetitions, getTimeRemaining } from '@/mocks/rfwCompetitions';
import { getUpcomingEvents, getTodayEvents } from '@/mocks/globalEvents';
import { trendingItems, directionConfig, categoryLabels } from '@/mocks/trendingRfw';
import { getAllClubs, clubsSummary } from '@/mocks/rfwClubs';

/* ─── Animated Counter ─── */
function AnimatedCounter({ value, suffix, duration = 1500 }: { value: number; suffix: string; duration?: number }) {
  const [display, setDisplay] = useState(0);
  const animRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    startRef.current = display;
    const diff = value - startRef.current;
    const start = performance.now();

    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startRef.current + diff * eased);
      setDisplay(current);
      if (progress < 1) {
        animRef.current = requestAnimationFrame(animate);
      }
    };

    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [value, duration]);

  const formatted = display >= 1000 ? (display / 1000).toFixed(1) + 'K' : display.toString();
  return <span>{formatted}{suffix}</span>;
}

/* ─── Live Pulse Dot ─── */
function PulseDot({ color }: { color: string }) {
  return (
    <span className="relative flex h-2 w-2">
      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${color} opacity-75`}></span>
      <span className={`relative inline-flex rounded-full h-2 w-2 ${color}`}></span>
    </span>
  );
}

/* ─── Trending Badge ─── */
function TrendBadge({ trend }: { trend: 'up' | 'down' | 'stable' }) {
  if (trend === 'up') return <span className="inline-flex items-center gap-0.5 text-emerald-600 text-xs font-semibold"><i className="ri-arrow-up-s-line text-sm"></i></span>;
  if (trend === 'down') return <span className="inline-flex items-center gap-0.5 text-rose-500 text-xs font-semibold"><i className="ri-arrow-down-s-line text-sm"></i></span>;
  return <span className="inline-flex items-center gap-0.5 text-foreground-400 text-xs"><i className="ri-subtract-line text-sm"></i></span>;
}

/* ═══════════════════════════════════════════════════
   LANDING VIVA SECTION
   ═══════════════════════════════════════════════════ */
export default function LandingVivaSection() {
  const navigate = useNavigate();
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>('hoy');
  const [selectedLiveTab, setSelectedLiveTab] = useState<'todos' | 'radios' | 'programas' | 'locutores'>('todos');
  const [scrolledPast, setScrolledPast] = useState(false);

  const activeCompetitions = getActiveCompetitions().slice(0, 4);
  const recentWinners = getActiveCompetitions().slice(0, 4);
  const upcomingEvents = getUpcomingEvents().slice(0, 4);
  const todayEvents = getTodayEvents();
  const topRadios = getTopRadiosForLanding();
  const topClubs = getAllClubs().sort((a, b) => b.clubScore - a.clubScore).slice(0, 5);

  const liveFiltered = selectedLiveTab === 'todos'
    ? liveHighlights
    : liveHighlights.filter(h => h.type === (selectedLiveTab === 'radios' ? 'radio' : selectedLiveTab === 'programas' ? 'programa' : 'locutor'));

  /* Animation trigger on scroll */
  useEffect(() => {
    const handleScroll = () => {
      const el = document.getElementById('landing-viva');
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.7) {
          setScrolledPast(true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* Filter trending by period */
  const filteredTrending = useCallback(() => {
    if (trendPeriod === 'hoy') return trendingItems.filter(t => t.trendDirection === 'exploding').slice(0, 6);
    if (trendPeriod === 'semana') return trendingItems.filter(t => t.trendDirection === 'exploding' || t.trendDirection === 'up').slice(0, 6);
    return trendingItems.slice(0, 6);
  }, [trendPeriod]);

  return (
    <section id="landing-viva" className="relative bg-background-50 pt-0">
      {/* ═══════════════ COUNTER STRIP ═══════════════ */}
      <div className="bg-background-100/70 border-b border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center gap-1.5 mb-3">
            <PulseDot color="bg-rose-400" />
            <span className="text-xs font-semibold text-rose-600 uppercase tracking-wider">En Vivo Ahora</span>
            <span className="text-xs text-foreground-400 ml-1">{landingVivaSummary.radiosEnVivo} radios · {landingVivaSummary.oyentesConectados.toLocaleString()} oyentes</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
            {liveCounters.map((counter) => (
              <div key={counter.id} className="flex items-center gap-2.5 bg-background-50 rounded-xl px-3 py-2.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-background-100`}>
                  <i className={`${counter.icon} ${counter.color} text-base`}></i>
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs text-foreground-400 leading-tight">{counter.label}</span>
                  <span className="text-sm font-bold text-foreground-950 tabular-nums">
                    {scrolledPast ? <AnimatedCounter value={counter.value} suffix="" /> : counter.value.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* ═══════════════ LIVE NOW STRIP ═══════════════ */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-foreground-950 uppercase tracking-wider">Transmitiendo Ahora</h3>
              <PulseDot color="bg-rose-400" />
            </div>
            <div className="flex bg-background-100 rounded-full p-0.5">
              {(['todos', 'radios', 'programas', 'locutores'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setSelectedLiveTab(tab)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    selectedLiveTab === tab
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {tab === 'todos' ? 'Todos' : tab === 'radios' ? 'Radios' : tab === 'programas' ? 'Programas' : 'Locutores'}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto pb-2 -mx-4 px-4">
            <div className="flex gap-3 min-w-max">
              {liveFiltered.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.linkUrl)}
                  className="flex-shrink-0 w-52 bg-background-50 rounded-xl border border-background-200/70 hover:border-background-300 transition-all cursor-pointer overflow-hidden group"
                >
                  <div className="relative h-28">
                    <img
                      src={item.thumbnailUrl}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
                    <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-foreground-950/60 backdrop-blur-sm">
                      <PulseDot color={item.pulseColor} />
                      <span className="text-[10px] font-bold text-background-50 uppercase tracking-wider">{item.statusLabel}</span>
                    </div>
                    <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-foreground-950/60 backdrop-blur-sm text-[10px] font-semibold text-background-50">
                      {item.metricValue >= 1000 ? (item.metricValue / 1000).toFixed(1) + 'K' : item.metricValue} {item.metricLabel}
                    </div>
                  </div>
                  <div className="p-3 text-left">
                    <p className="text-xs font-bold text-foreground-950 truncate">{item.name}</p>
                    <p className="text-[11px] text-foreground-500 truncate mt-0.5">{item.subtitle}</p>
                  </div>
                </button>
              ))}
              <button
                onClick={() => navigate('/radios-en-vivo')}
                className="flex-shrink-0 w-20 bg-background-100 rounded-xl border border-dashed border-background-300 hover:border-background-400 hover:bg-background-200/50 transition-all cursor-pointer flex flex-col items-center justify-center gap-1.5"
              >
                <i className="ri-arrow-right-line text-foreground-400 text-lg"></i>
                <span className="text-[10px] text-foreground-400 font-medium whitespace-nowrap">Ver todo</span>
              </button>
            </div>
          </div>
        </div>

        {/* ═══════════════ 3-COLUMN MAIN GRID ═══════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ─── LEFT: TOP RANKINGS ─── */}
          <div className="lg:col-span-1 space-y-5">
            <div className="bg-background-100/60 rounded-2xl p-5 border border-background-200/60">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-bold text-foreground-950 uppercase tracking-wider flex items-center gap-1.5">
                  <i className="ri-trophy-line text-amber-500 text-sm"></i>Top Rankings
                </h4>
                <button
                  onClick={() => navigate('/rankings')}
                  className="text-[11px] text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-0.5"
                >
                  Ver todos <i className="ri-arrow-right-s-line text-xs"></i>
                </button>
              </div>
              <div className="space-y-3">
                {topRadios.map((radio) => (
                  <button
                    key={radio.position}
                    onClick={() => navigate('/radio/' + radio.subtitle.replace(/[.\s]+RFW$/g, '').replace(/\./g, '-').toLowerCase())}
                    className="flex items-center gap-3 w-full text-left p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className={`text-xs font-bold w-5 text-center ${radio.position === 1 ? 'text-amber-500' : radio.position === 2 ? 'text-foreground-400' : radio.position === 3 ? 'text-orange-400' : 'text-foreground-500'}`}>
                      #{radio.position}
                    </span>
                    <img src={radio.logoUrl} alt={radio.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground-900 truncate">{radio.name}</p>
                      <p className="text-[10px] text-foreground-400 truncate">{radio.subtitle}</p>
                    </div>
                    <div className="flex items-center gap-1 text-right flex-shrink-0">
                      <span className="text-xs font-bold text-foreground-900">{radio.score.toLocaleString()}</span>
                      <TrendBadge trend={radio.trend} />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Clubs activos */}
            <div className="bg-background-100/60 rounded-2xl p-5 border border-background-200/60">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-bold text-foreground-950 uppercase tracking-wider flex items-center gap-1.5">
                  <i className="ri-vip-crown-line text-purple-500 text-sm"></i>Clubes Activos
                </h4>
                <button
                  onClick={() => navigate('/clubes')}
                  className="text-[11px] text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-0.5"
                >
                  Ver todos <i className="ri-arrow-right-s-line text-xs"></i>
                </button>
              </div>
              <div className="space-y-2.5">
                {topClubs.map((club) => (
                  <button
                    key={club.id}
                    onClick={() => navigate('/clubes')}
                    className="flex items-center gap-3 w-full text-left p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <img src={club.thumbnailUrl} alt={club.clubName} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground-900 truncate">{club.clubName}</p>
                      <p className="text-[10px] text-foreground-400 truncate">{club.membersCount.toLocaleString()} miembros · #{club.rankingPosition}</p>
                    </div>
                    <span className="text-xs font-bold text-foreground-900">{club.clubScore.toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ─── CENTER: TRENDS + COMPETITIONS ─── */}
          <div className="lg:col-span-1 space-y-5">
            {/* Trends */}
            <div className="bg-background-100/60 rounded-2xl p-5 border border-background-200/60">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-bold text-foreground-950 uppercase tracking-wider flex items-center gap-1.5">
                  <i className="ri-fire-line text-orange-500 text-sm"></i>Tendencias
                </h4>
                <div className="flex bg-background-200/70 rounded-full p-0.5">
                  {(['hoy', 'semana', 'mes'] as TrendPeriod[]).map((p) => (
                    <button
                      key={p}
                      onClick={() => setTrendPeriod(p)}
                      className={`px-2.5 py-1 text-[10px] font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                        trendPeriod === p
                          ? 'bg-background-50 text-foreground-950 shadow-sm'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {p === 'hoy' ? 'Hoy' : p === 'semana' ? 'Semana' : 'Mes'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2.5">
                {filteredTrending().map((item) => {
                  const d = directionConfig[item.trendDirection];
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.category === 'radios' ? '/explorar' : item.category === 'locutores' ? '/locutores-en-vivo' : item.category === 'programas' ? '/programas-en-vivo' : item.category === 'eventos' ? '/eventos' : '/competencias')}
                      className="flex items-center gap-3 w-full text-left p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="text-[10px] font-bold text-foreground-400 w-4">#{item.rank}</span>
                      <img src={item.thumbnailUrl} alt={item.name} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground-900 truncate">{item.name}</p>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-foreground-400">{categoryLabels[item.category]}</span>
                          <span className={`text-[10px] font-semibold ${d.color}`}>+{item.growthPercent}%</span>
                        </div>
                      </div>
                      <span className={`${d.bg} ${d.color} px-1.5 py-0.5 rounded text-[10px] font-semibold flex items-center gap-0.5`}>
                        <i className={`${d.icon} text-[10px]`}></i>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Competencias activas */}
            <div className="bg-background-100/60 rounded-2xl p-5 border border-background-200/60">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-bold text-foreground-950 uppercase tracking-wider flex items-center gap-1.5">
                  <i className="ri-sword-line text-rose-500 text-sm"></i>Competencias Activas
                </h4>
                <button
                  onClick={() => navigate('/competencias')}
                  className="text-[11px] text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-0.5"
                >
                  Ver todas <i className="ri-arrow-right-s-line text-xs"></i>
                </button>
              </div>
              <div className="space-y-3">
                {activeCompetitions.map((comp) => {
                  const pct = comp.totalVotes > 0 ? Math.round((comp.challenger.votes / comp.totalVotes) * 100) : 50;
                  return (
                    <button
                      key={comp.id}
                      onClick={() => navigate('/competencias')}
                      className="w-full text-left bg-background-50 rounded-xl p-3 border border-background-200/60 hover:border-background-300 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-rose-600 uppercase bg-rose-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <PulseDot color="bg-rose-400" />EN VIVO
                        </span>
                        <span className="text-[10px] text-foreground-400">{getTimeRemaining(comp.endDate)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-foreground-900 flex-1 truncate">{comp.challenger.name}</span>
                        <span className="text-[10px] font-bold text-foreground-400">VS</span>
                        <span className="text-[11px] font-semibold text-foreground-900 flex-1 truncate text-right">{comp.opponent.name}</span>
                      </div>
                      <div className="mt-2 h-1.5 bg-background-100 rounded-full overflow-hidden">
                        <div className="h-full bg-rose-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }}></div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[10px] text-foreground-500">{comp.challenger.votes.toLocaleString()} votos</span>
                        <span className="text-[10px] text-foreground-500">{comp.opponent.votes.toLocaleString()} votos</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ─── RIGHT: CTAs + ACTIVITY FEED ─── */}
          <div className="lg:col-span-1 space-y-5">
            {/* CTAs */}
            <div className="space-y-2.5">
              {landingCTAs.map((cta) => (
                <button
                  key={cta.id}
                  onClick={() => navigate(cta.linkUrl)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl transition-all cursor-pointer group ${cta.bgClass} ${cta.textClass}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-background-50/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className={`${cta.icon} text-lg`}></i>
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-sm font-bold">{cta.label}</p>
                    <p className="text-xs opacity-80 mt-0.5">{cta.subtitle}</p>
                  </div>
                  <i className="ri-arrow-right-line text-lg opacity-60 group-hover:translate-x-1 transition-transform"></i>
                </button>
              ))}
            </div>

            {/* Activity Feed */}
            <div className="bg-background-100/60 rounded-2xl p-5 border border-background-200/60">
              <h4 className="text-xs font-bold text-foreground-950 uppercase tracking-wider flex items-center gap-1.5 mb-4">
                <i className="ri-flashlight-line text-amber-500 text-sm"></i>Actividad Reciente
              </h4>
              <div className="space-y-3">
                {activityFeed.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.linkUrl)}
                    className="flex items-start gap-2.5 w-full text-left group cursor-pointer"
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-background-100`}>
                      <i className={`${item.icon} ${item.iconColor} text-xs`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.text }}></p>
                      <p className="text-[10px] text-foreground-400 mt-0.5">{item.timeAgo}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Eventos Hoy */}
            {todayEvents.length > 0 && (
              <div className="bg-background-100/60 rounded-2xl p-5 border border-background-200/60">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xs font-bold text-foreground-950 uppercase tracking-wider flex items-center gap-1.5">
                    <i className="ri-calendar-event-line text-teal-500 text-sm"></i>Eventos Hoy
                    <span className="bg-teal-100 text-teal-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{todayEvents.length}</span>
                  </h4>
                  <button
                    onClick={() => navigate('/eventos')}
                    className="text-[11px] text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-0.5"
                  >
                    Todos <i className="ri-arrow-right-s-line text-xs"></i>
                  </button>
                </div>
                <div className="space-y-2.5">
                  {todayEvents.slice(0, 3).map((evt) => (
                    <button
                      key={evt.id}
                      onClick={() => navigate('/eventos')}
                      className="flex items-center gap-3 w-full text-left p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <img src={evt.imageUrl} alt={evt.title} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" loading="lazy" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground-900 truncate">{evt.title}</p>
                        <p className="text-[10px] text-foreground-400 truncate">{evt.stationName} · {evt.city}</p>
                      </div>
                      <span className="text-[10px] font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full whitespace-nowrap">HOY</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════ BOTTOM ROW: UPCOMING EVENTS ═══════════════ */}
        {upcomingEvents.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-foreground-950 uppercase tracking-wider flex items-center gap-1.5">
                <i className="ri-calendar-line text-sky-500 text-sm"></i>Próximos Eventos
              </h3>
              <button
                onClick={() => navigate('/eventos')}
                className="text-[11px] text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-0.5"
              >
                Ver calendario completo <i className="ri-arrow-right-s-line text-xs"></i>
              </button>
            </div>
            <div className="overflow-x-auto pb-2 -mx-4 px-4">
              <div className="flex gap-3 min-w-max">
                {upcomingEvents.map((evt) => {
                  const eventDate = new Date(evt.date);
                  const months = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
                  return (
                    <button
                      key={evt.id}
                      onClick={() => navigate('/eventos')}
                      className="flex-shrink-0 w-64 bg-background-50 rounded-xl border border-background-200/70 hover:border-background-300 transition-all cursor-pointer overflow-hidden group text-left"
                    >
                      <div className="relative h-32">
                        <img
                          src={evt.imageUrl}
                          alt={evt.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent"></div>
                        <div className="absolute top-2 right-2 bg-background-50 rounded-lg px-2 py-1 text-center">
                          <span className="block text-[10px] font-bold text-foreground-900 leading-tight">{eventDate.getDate()}</span>
                          <span className="block text-[9px] text-foreground-400 leading-tight">{months[eventDate.getMonth()]}</span>
                        </div>
                      </div>
                      <div className="p-3">
                        <p className="text-xs font-bold text-foreground-950 line-clamp-1">{evt.title}</p>
                        <p className="text-[11px] text-foreground-500 mt-0.5 line-clamp-1">{evt.stationName} · {evt.city}, {evt.state}</p>
                        <div className="flex items-center gap-1.5 mt-2">
                          <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">{evt.time}</span>
                          <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">{evt.modality === 'presencial' ? 'Presencial' : evt.modality === 'online' ? 'Online' : 'Híbrido'}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
                <button
                  onClick={() => navigate('/eventos')}
                  className="flex-shrink-0 w-20 bg-background-100 rounded-xl border border-dashed border-background-300 hover:border-background-400 hover:bg-background-200/50 transition-all cursor-pointer flex flex-col items-center justify-center gap-1.5"
                >
                  <i className="ri-arrow-right-line text-foreground-400 text-lg"></i>
                  <span className="text-[10px] text-foreground-400 font-medium whitespace-nowrap">Ver más</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}